/* ==========================================================================
   Raid — core theme behaviour
   Cart (AJAX add, drawer, quantity, remove), mobile nav, sticky buy bar,
   predictive search.
   ========================================================================== */
(function () {
  'use strict';

  var T = window.RaidTheme || {};
  var routes = T.routes || {};

  /* ---------- helpers ---------- */
  function money(cents, format) {
    var fmt = format || T.moneyFormat || '${{amount}}';
    function num(value, precision) {
      if (isNaN(value)) value = 0;
      var parts = (value / 100).toFixed(precision === undefined ? 2 : precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
      return parts[1] ? whole + '.' + parts[1] : whole;
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      if (name === 'amount_no_decimals') return num(cents, 0);
      if (name === 'amount_with_comma_separator') return num(cents, 2).replace(/,/g, '§').replace(/\./g, ',').replace(/§/g, '.');
      if (name === 'amount_no_decimals_with_comma_separator') return num(cents, 0).replace(/,/g, '.');
      return num(cents, 2);
    });
  }

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (r) {
      if (!r.ok) return r.json().then(function (body) { throw body; });
      return r.json();
    });
  }

  /* ==========================================================================
     Cart
     ========================================================================== */
  var Cart = {
    drawer: null,
    scrim: null,

    init: function () {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.scrim = document.querySelector('[data-cart-scrim]');

      var self = this;

      // open/close
      document.addEventListener('click', function (e) {
        var opener = e.target.closest('[data-cart-open]');
        if (opener && T.cartType === 'drawer') { e.preventDefault(); self.open(); }

        if (e.target.closest('[data-cart-close]')) { e.preventDefault(); self.close(); }
      });

      if (this.scrim) this.scrim.addEventListener('click', function () { self.close(); });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') self.close();
      });

      // add to cart
      document.addEventListener('submit', function (e) {
        var form = e.target.closest('form[data-product-form]');
        if (!form) return;
        e.preventDefault();
        self.add(form);
      });

      // quantity + remove, delegated so re-rendered markup keeps working
      document.addEventListener('click', function (e) {
        var step = e.target.closest('[data-qty-step]');
        if (step) {
          e.preventDefault();
          var input = step.parentElement.querySelector('input');
          var delta = parseInt(step.dataset.qtyStep, 10);
          var next = Math.max(0, (parseInt(input.value, 10) || 0) + delta);
          input.value = next;
          self.change(input.dataset.lineKey, next);
        }

        var remove = e.target.closest('[data-cart-remove]');
        if (remove) {
          e.preventDefault();
          self.change(remove.dataset.lineKey, 0);
        }
      });

      document.addEventListener('change', function (e) {
        var input = e.target.closest('[data-qty-input]');
        if (!input) return;
        self.change(input.dataset.lineKey, Math.max(0, parseInt(input.value, 10) || 0));
      });
    },

    open: function () {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      if (this.scrim) this.scrim.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      var close = this.drawer.querySelector('[data-cart-close]');
      if (close) close.focus();
    },

    close: function () {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      if (this.scrim) this.scrim.classList.remove('is-open');
      document.body.style.overflow = '';
    },

    add: function (form) {
      var self = this;
      var btn = form.querySelector('[data-add-button]');
      var label = btn && (btn.querySelector('[data-add-label]') || btn);
      var original = label ? label.textContent : '';

      if (btn) { btn.disabled = true; }
      if (label) label.textContent = '…';

      var body = new FormData(form);

      fetchJSON(routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: body
      })
        .then(function () {
          if (label) label.textContent = T.strings.added || 'Added';
          return self.refresh();
        })
        .then(function () {
          if (T.cartType === 'drawer') self.open();
          else window.location.href = routes.cart_url;
        })
        .catch(function (err) {
          var msg = (err && err.description) || (err && err.message) || 'Could not add to cart';
          self.showError(form, msg);
          if (label) label.textContent = original;
        })
        .then(function () {
          setTimeout(function () {
            if (btn) btn.disabled = false;
            if (label && label.textContent === (T.strings.added || 'Added')) label.textContent = original;
          }, 1400);
        });
    },

    change: function (key, quantity) {
      if (!key) return;
      var self = this;

      fetchJSON(routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function (cart) {
          self.render(cart);
          // the full cart page needs its own markup refreshed
          if (document.body.classList.contains('template-cart')) window.location.reload();
        })
        .catch(function () { window.location.reload(); });
    },

    refresh: function () {
      var self = this;
      return fetchJSON(routes.cart_url + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (cart) { self.render(cart); return cart; });
    },

    render: function (cart) {
      // bubble count
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = cart.item_count;
      });

      // drawer contents
      var list = document.querySelector('[data-cart-items]');
      if (list) {
        if (!cart.items.length) {
          list.innerHTML = '<div class="drawer-empty">' + (T.strings.cartEmpty || 'Your cart is empty.') + '</div>';
        } else {
          list.innerHTML = cart.items.map(function (item) {
            var img = item.image
              ? '<img src="' + item.image.replace(/(\.[^.]*)$/, '_120x$1') + '" alt="" loading="lazy">'
              : '';
            var variant = item.variant_title && item.variant_title !== 'Default Title'
              ? '<p>' + item.variant_title + '</p>' : '';
            return '' +
              '<div class="drawer-item">' +
                '<div class="drawer-item__media">' + img + '</div>' +
                '<div>' +
                  '<h4>' + item.product_title + '</h4>' + variant +
                  '<button type="button" class="cart-remove" data-cart-remove data-line-key="' + item.key + '">Remove</button>' +
                '</div>' +
                '<div class="drawer-item__price">' + money(item.final_line_price) + '</div>' +
              '</div>';
          }).join('');
        }
      }

      var total = document.querySelector('[data-cart-total]');
      if (total) total.textContent = money(cart.total_price);

      var checkout = document.querySelector('[data-cart-checkout]');
      if (checkout) checkout.hidden = cart.item_count === 0;
    },

    showError: function (form, message) {
      var box = form.querySelector('[data-form-error]');
      if (!box) return;
      box.textContent = message;
      box.hidden = false;
      setTimeout(function () { box.hidden = true; }, 6000);
    }
  };

  /* ==========================================================================
     Mobile navigation
     ========================================================================== */
  function initNav() {
    var burger = document.querySelector('[data-burger]');
    var panel = document.querySelector('[data-mobile-nav]');

    if (burger && panel) {
      burger.addEventListener('click', function () {
        var open = !panel.hasAttribute('hidden');
        if (open) panel.setAttribute('hidden', '');
        else panel.removeAttribute('hidden');
        burger.setAttribute('aria-expanded', String(!open));
      });
    }

    // desktop dropdowns — open on hover, and on click/keyboard for touch + a11y
    document.querySelectorAll('[data-nav-group]').forEach(function (group) {
      var toggle = group.querySelector('[data-nav-toggle]');
      var menu = group.querySelector('[data-nav-panel]');
      if (!toggle || !menu) return;

      function open() {
        closeAll();
        menu.removeAttribute('hidden');
        toggle.setAttribute('aria-expanded', 'true');
      }
      function close() {
        menu.setAttribute('hidden', '');
        toggle.setAttribute('aria-expanded', 'false');
      }

      group.addEventListener('mouseenter', open);
      group.addEventListener('mouseleave', close);

      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        if (menu.hasAttribute('hidden')) open();
        else close();
      });

      group.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { close(); toggle.focus(); }
      });

      // close when focus leaves the group entirely
      group.addEventListener('focusout', function (e) {
        if (!group.contains(e.relatedTarget)) close();
      });
    });

    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-nav-group]')) closeAll();
    });
  }

  function closeAll() {
    document.querySelectorAll('[data-nav-panel]').forEach(function (m) {
      m.setAttribute('hidden', '');
    });
    document.querySelectorAll('[data-nav-toggle]').forEach(function (t) {
      t.setAttribute('aria-expanded', 'false');
    });
  }

  /* ==========================================================================
     Sticky mobile buy bar
     ========================================================================== */
  function initStickyBuy() {
    var bar = document.querySelector('[data-sticky-buy]');
    var anchor = document.querySelector('[data-add-button]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;

    new IntersectionObserver(function (entries) {
      bar.classList.toggle('is-visible', !entries[0].isIntersecting);
    }, { threshold: 0 }).observe(anchor);

    var proxy = bar.querySelector('[data-sticky-add]');
    if (proxy) {
      proxy.addEventListener('click', function () { anchor.click(); });
    }
  }

  /* ==========================================================================
     Predictive search
     ========================================================================== */
  function initSearch() {
    var input = document.querySelector('[data-predictive-input]');
    var results = document.querySelector('[data-predictive-results]');
    if (!input || !results || !routes.predictive_search_url) return;

    var timer = null;

    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(timer);

      if (q.length < 2) { results.innerHTML = ''; results.hidden = true; return; }

      timer = setTimeout(function () {
        var url = routes.predictive_search_url +
          '?q=' + encodeURIComponent(q) +
          '&resources[type]=product&resources[limit]=5&section_id=predictive-search';

        fetch(url)
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var block = doc.querySelector('[data-predictive-inner]');
            results.innerHTML = block ? block.innerHTML : '';
            results.hidden = !block;
          })
          .catch(function () { results.hidden = true; });
      }, 220);
    });

    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-predictive-wrap]')) results.hidden = true;
    });
  }

  /* ==========================================================================
     Boot
     ========================================================================== */
  function boot() {
    Cart.init();
    initNav();
    initStickyBuy();
    initSearch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // theme editor support
  document.addEventListener('shopify:section:load', function () {
    initStickyBuy();
    initSearch();
    initNav();
  });

  window.RaidCart = Cart;
})();
